import React from 'react';
import { LucideIcon } from 'lucide-react';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual variant of the button */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Optional icon to display */
  icon?: LucideIcon;
  /** Position of the icon relative to text */
  iconPosition?: 'left' | 'right';
  /** Loading state */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Button content */
  children?: React.ReactNode;
}

/**
 * Button Atom Component
 * 
 * A fundamental UI element for user interactions.
 * 
 * @example
 * ```tsx
 * // Primary button
 * <Button variant="primary">Save</Button>
 * 
 * // Button with icon
 * <Button variant="secondary" icon={Plus}>Add Item</Button>
 * 
 * // Loading state
 * <Button loading>Processing...</Button>
 * ```
 */
export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  className = '',
  children,
  disabled,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  const variantClasses = {
    primary: 'bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500 border border-transparent',
    secondary: 'bg-white hover:bg-secondary-50 text-secondary-700 focus:ring-primary-500 border border-secondary-300',
    outline: 'bg-transparent hover:bg-secondary-50 text-secondary-700 focus:ring-primary-500 border border-secondary-300',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 border border-transparent',
    ghost: 'bg-transparent hover:bg-secondary-100 text-secondary-600 hover:text-secondary-700 focus:ring-primary-500 border border-transparent',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  const isDisabled = disabled || loading;

  const classes = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    isDisabled && 'opacity-50 cursor-not-allowed',
    className,
  ].filter(Boolean).join(' ');

  const renderIcon = () => {
    if (loading) {
      return (
        <svg className={`animate-spin ${iconSizes[size]} ${iconPosition === 'right' && children ? 'ml-2' : children ? 'mr-2' : ''}`} fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }

    if (Icon) {
      return (
        <Icon 
          className={`${iconSizes[size]} ${iconPosition === 'right' && children ? 'ml-2' : children ? 'mr-2' : ''}`} 
        />
      );
    }

    return null;
  };

  return (
    <button
      className={classes}
      disabled={isDisabled}
      {...props}
    >
      {iconPosition === 'left' && renderIcon()}
      {children}
      {iconPosition === 'right' && renderIcon()}
    </button>
  );
};
